import { Plugin } from 'ckeditor5/src/core';
import type { DropdownButtonView } from 'ckeditor5/src/ui';
import { createDropdown, addListToDropdown, ViewModel } from 'ckeditor5/src/ui';
import { Collection } from 'ckeditor5/src/utils';

import '../../theme/notes.css';

// Define proper types for dropdown items
interface NoteTypeDefinition {
	type: string;
	label: string;
}

/**
 * The note UI feature.
 */
export default class MgnlNoteUI extends Plugin {
	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'MgnlNoteUI' as const;
	}

	/**
	 * @inheritDoc
	 */
	public init(): void {
		console.log('MgnlNoteUI plugin initializing');

		const editor = this.editor;
		const t = editor.t;

		// Check for any conflicting command and disable it
		const potentialConflicts = ['note', 'noteBlock', 'blockNote'];
		potentialConflicts.forEach((cmdName) => {
			const cmd = editor.commands.get(cmdName);
			if (cmd) {
				console.log(
					`Found potentially conflicting command: ${cmdName}, disabling it`
				);
				cmd.forceDisabled('mgnlNote');
			}
		});

		// Only register if not already registered
		if (!editor.ui.componentFactory.has('mgnlNote')) {
			// Register the UI button
			editor.ui.componentFactory.add('mgnlNote', (locale) => {
				console.log('Creating mgnlNote dropdown');

				const dropdownView = createDropdown(locale);
				const command = editor.commands.get('mgnlNote');

				if (!command) {
					console.error(
						'MgnlNote command not found - make sure MgnlNoteEditing is loaded properly'
					);
				}

				// Create dropdown with button that has a text label
				const buttonView =
					dropdownView.buttonView as DropdownButtonView;

				// AVOID BINDING - set properties directly instead
				buttonView.set({
					label: t('Notes'), // Changed from "Note Type" to just "Notes"
					tooltip: true,
					withText: true, // Show text label
					class: 'ck-mgnl-note-dropdown',
					isEnabled: true, // Set enabled by default
				});

				// Only update enabled state manually - NO BINDING
				if (command) {
					// Initial state
					buttonView.isEnabled = command.isEnabled;

					// Listen for changes
					this.listenTo(command, 'change:isEnabled', () => {
						buttonView.isEnabled = command.isEnabled;
					});
				}

				// Define note types - just the 4 specified types
				const noteTypes: Array<NoteTypeDefinition> = [
					{ type: 'info', label: t('Note Info') },
					{ type: 'tip', label: t('Note Tip') },
					{ type: 'caution', label: t('Note Caution') },
					{ type: 'alert', label: t('Note Alert') },
				];

				// Create properly typed dropdown items
				const items = new Collection();

				// Add each type to the collection with proper types
				noteTypes.forEach((item) => {
					const model = new ViewModel({
						label: item.label,
						class: `ck-mgnl-notetype-item ck-mgnl-notetype-${item.type}`,
						withText: true,
						commandParams: { type: item.type },
						tooltip: item.label,
					});

					items.add({
						type: 'button',
						model,
					});
				});

				// Add the items to the dropdown with proper typing
				addListToDropdown(dropdownView, items as any, {
					ariaLabel: t('Note Types List'),
				});

				// Execute the command when a dropdown item is clicked
				this.listenTo(dropdownView, 'execute', (evt) => {
					console.log('Note dropdown execute', evt);

					const source = evt.source as any;

					// Safely access commandParams
					if (source && source.commandParams && command) {
						editor.execute('mgnlNote', {
							type: source.commandParams.type,
						});
					}
					editor.editing.view.focus();
				});

				console.log('MgnlNote dropdown created');
				return dropdownView;
			});
		} else {
			console.warn('MgnlNote UI component already registered');
		}

		console.log('MgnlNoteUI plugin initialized');
	}
}
